import {
  Flex,
  Box,
  Button,
  IconButton,
  Spinner,
  Text,
  Link,
} from '@theme-ui/components'
import { useFormikContext, Form } from 'formik'
import { isEmpty } from 'lodash'
import React, { useContext, useState } from 'react'
import { FaFileDownload, FaPlus, FaTrash } from 'react-icons/fa'
import { useMutation } from 'react-query'
import { match, when } from 'ts-pattern'
import { uploadFile, deleteFile } from '../../../api/reportFunction'
import { Table } from '../../../components'
import InputFile from '../../../components/Input/InputFile'
import { TextareaComponent } from '../../../components/Input/Textarea'
import { notifyErrorAPI, notifyError } from '../../../components/Notif'
import { PanelComponent } from '../../../components/Panel/Panel'
import useCurrentUserStore from '../../../stores/currentUser'
import { matchRegExp } from '../../../utils'
import { fileToBase64 } from '../../../utils/file'
import { date } from '../../../utils/formatter'
import { AttachmentsNote } from './Notes'
import { ReportContext, ReportFormValues } from './ReportForm'

function Attachments() {
  const context = useContext(ReportContext)
  const report = context.report
  const roles = useCurrentUserStore((s) => s.currentUser.data?.roles)
  const [file, setFile] = useState<File | null>(null)
  const [desc, setDesc] = useState('')
  const { values, setFieldValue, errors } = useFormikContext<ReportFormValues>()
  const attachments = values.attachments?.attachments || []

  const uploadM = useMutation((values: any) => uploadFile(values), {
    onError: notifyErrorAPI,
  })

  const deleteAttachM = useMutation(deleteFile)

  function addFile(e) {
    e.preventDefault()
    e.stopPropagation()

    if (file.size > 20971520) return notifyError('Ukuran file terlalu besar')

    fileToBase64(file)
      .then(async (b64) => {
        const response = await uploadM.mutateAsync({
          attachment: {
            fileName: file.name,
            notes: desc,
            file: b64,
          },
        })

        const newFile: ReportFormValues['attachments']['attachments'][0] = {
          id: response?.data?.data?.id,
          fileName: file.name,
          notes: desc,
          urlPath: response?.data?.data?.urlPath,
          jenisFile: response?.data?.data?.contentType,
          size: file.size,
          createdAt: response?.data?.data?.createdAt,
        }

        setFieldValue('attachments.attachments', [...attachments, newFile])
        setFile(null)
        setDesc('')
      })
      .catch(({ error }) => {
        notifyError(error)
      })
  }

  return (
    <>
      <PanelComponent
        id="attachments"
        header="Dokumen Pendukung"
        variant={errors.attachments?.attachments ? 'error' : 'primary'}
        childPadding={[1, null, null]}
      >
        <Text color="error">{errors.attachments?.attachments}</Text>
        {context.state === 'editing' && (
          <Form onSubmitCapture={addFile}>
            <Flex sx={{ alignItems: 'end', gap: 2 }}>
              <Box sx={{ flex: 2 }}>
                <InputFile
                  required
                  onChange={({ base64, e }) => setFile(e.target.files?.[0])}
                  loading={uploadM.isLoading}
                />
              </Box>
              <Box sx={{ flex: 2 }}>
                <TextareaComponent
                  required
                  label="Keterangan"
                  onChange={(e) => setDesc(e.target.value)}
                  value={desc}
                  sx={{ height: 55 }}
                />
              </Box>
              <Box sx={{ flex: 1, textAlign: 'right' }}>
                <Button type="submit" disabled={uploadM.isLoading}>
                  <Box as={FaPlus} pt="1" />
                  <Box as="span" sx={{ display: ['none', 'inline'] }}>
                    Tambah
                  </Box>
                </Button>
              </Box>
            </Flex>
          </Form>
        )}

        {!isEmpty(attachments) && (
          <Table
            disablePagination
            options={{
              initialState: { pageSize: 5 },
              columns: [
                { Header: 'No', width: '5%', Cell: ({ row }) => row.index + 1 },
                { Header: 'Nama File', accessor: 'fileName', Cell: FileCell },
                {
                  Header: 'Timestamp',
                  Cell: ({ row: { original } }) => date(original.createdAt),
                },
                { Header: 'Jenis File', accessor: 'jenisFile' },
                { Header: 'Keterangan', accessor: 'notes' },
                ...(context.state === 'editing'
                  ? [
                      {
                        Header: 'Aksi',
                        Cell: function (props) {
                          const id = props.row.original.id

                          const handleDelete = async () => {
                            deleteAttachM
                              .mutateAsync(id)
                              .then(() => {
                                const filtered = attachments.filter(
                                  (data) => data.id !== id
                                )
                                setFieldValue(
                                  'attachments.attachments',
                                  filtered
                                )
                                setFieldValue(
                                  'deleteAttachmentByIds',
                                  (prev) => [...prev, id]
                                )
                              })
                              .catch(notifyErrorAPI)
                          }

                          return (
                            <IconButton
                              onClick={handleDelete}
                              disabled={deleteAttachM.isLoading}
                            >
                              {deleteAttachM.isLoading ? (
                                <Spinner />
                              ) : (
                                <FaTrash size={45} />
                              )}
                            </IconButton>
                          )
                        },
                        width: '10%',
                      },
                    ]
                  : []),
              ],
              data: attachments,
            }}
          />
        )}
      </PanelComponent>
      {match([roles, report?.state])
        .with(
          [when(matchRegExp(/undefined|user|admin_verificator/)), undefined],
          () => <AttachmentsNote />
        )
        .otherwise(() => null)}
    </>
  )
}

function FileCell({ cell, row, ...props }) {
  return (
    <Link
      href={row.original.urlPath}
      download={row.original.urlPath}
      target="_blank"
    >
      {cell.value}
      <Box as={FaFileDownload} ml="1" mb={-1} />
    </Link>
  )
}

export default Attachments
